/**
 * SOUND-784 — Kurv pricing templates, configured on the PROCESSOR record.
 *
 * CPO: templates cannot be pulled from Stratus, so they are CRM configuration.
 * sound-api stores them as `processors.pricingTemplates`
 * `[{ templateId, name, isDefault, active }]` and `PUT /processors/:id` takes the
 * array as a WHOLE-ARRAY REPLACE — so the editor always sends every row, and a
 * row is deactivated rather than deleted once it has been saved (a MID
 * application may already have been sent with it).
 *
 * The rules mirror the API's validator, so a refusal is caught before the
 * round trip; the API stays the enforcer:
 *   - name required
 *   - templateId unique per processor
 *   - at most one isDefault
 *   - the default must be active
 * Plus one client-side rule: templateId required — a template with no id
 * cannot be sent to Kurv. (ASSUMPTION: the contract did not state it.)
 *
 * Pure and tested: sound-app has no component-test harness (SOUND-679).
 */

const str = (v) => (v === undefined || v === null ? "" : String(v));

/** A server row → an editor row. `active` defaults to true when absent. */
export function normalizePricingTemplates(list) {
  return (Array.isArray(list) ? list : [])
    .filter((t) => t && typeof t === "object")
    .map((t) => ({
      templateId: str(t.templateId),
      // The id exactly as the server sent it — a Number when numeric (Kurv's
      // is 239). An unedited row sends this back untouched.
      originalTemplateId: t.templateId,
      name: str(t.name),
      isDefault: t.isDefault === true,
      active: t.active !== false,
      // Saved rows are deactivated, never removed — see the header.
      isNew: false,
    }));
}

export function blankPricingTemplate() {
  return { templateId: "", name: "", isDefault: false, active: true, isNew: true };
}

/** Ids are compared as strings, the same way the API does ("239" == 239). */
export function sameTemplateId(a, b) {
  if (a === null || a === undefined || b === null || b === undefined) return false;
  return String(a).trim() === String(b).trim();
}

/** Edit one field of one row. Returns a new array. */
export function updateTemplate(list, index, field, value) {
  return list.map((t, i) => (i === index ? { ...t, [field]: value } : t));
}

/**
 * Make row `index` THE default. Clears every other default, and activates the
 * row — the default must be active, so choosing it is choosing to use it.
 */
export function setDefaultTemplate(list, index) {
  return list.map((t, i) => (i === index ? { ...t, isDefault: true, active: true } : { ...t, isDefault: false }));
}

/** Flip active. Deactivating the default also clears its default flag. */
export function toggleTemplateActive(list, index) {
  return list.map((t, i) => {
    if (i !== index) return t;
    const active = !t.active;
    return { ...t, active, isDefault: active ? t.isDefault : false };
  });
}

/** Only an unsaved row may be removed outright. */
export function removeTemplate(list, index) {
  return list[index]?.isNew ? list.filter((_, i) => i !== index) : list;
}

/**
 * Validate the whole list.
 * @returns {{ valid: boolean, rowErrors: Record<number, Record<string,string>>, formError: string|null }}
 */
export function validatePricingTemplates(list) {
  const rowErrors = {};
  const add = (i, field, message) => {
    rowErrors[i] = { ...(rowErrors[i] || {}), [field]: rowErrors[i]?.[field] || message };
  };
  const seen = new Map();
  (list || []).forEach((t, i) => {
    if (!str(t.name).trim()) add(i, "name", "Name is required");
    const id = str(t.templateId).trim();
    if (!id) add(i, "templateId", "Template ID is required");
    else if (seen.has(id)) {
      add(i, "templateId", "Template ID is already used on this processor");
      add(seen.get(id), "templateId", "Template ID is already used on this processor");
    } else seen.set(id, i);
    if (t.isDefault && !t.active) add(i, "isDefault", "The default template must be active");
  });
  const defaults = (list || []).filter((t) => t.isDefault).length;
  const formError = defaults > 1 ? "Only one template can be the default" : null;
  return { valid: Object.keys(rowErrors).length === 0 && !formError, rowErrors, formError };
}

/** The `pricingTemplates` value for the PUT: every row, trimmed, no UI flags. */
export function pricingTemplatesPayload(list) {
  return (list || []).map((t) => ({
    // Unchanged id → exactly as received (Number stays Number). An edited or
    // new id goes as a trimmed string; the API types a numeric string itself.
    templateId:
      t.originalTemplateId !== undefined && String(t.originalTemplateId) === str(t.templateId).trim()
        ? t.originalTemplateId
        : str(t.templateId).trim(),
    name: str(t.name).trim(),
    isDefault: Boolean(t.isDefault),
    active: Boolean(t.active),
  }));
}

/**
 * A 400 from the PUT → errors keyed by row. The API sends
 * `{ error: "Validation failed", details: [{ index, field, message }] }`; an
 * ARRAY-level problem has `index: null, field: "pricingTemplates"` and belongs
 * to the section, not a row. Details whose `field` is not a row field (e.g. an
 * unknown key) also fold into `formError`. A plain `{ error }` is tolerated.
 */
export function pricingTemplateServerErrors(body) {
  const rowErrors = {};
  const loose = [];
  const details = Array.isArray(body?.details) ? body.details : [];
  for (const d of details) {
    if (!d || typeof d !== "object") {
      if (typeof d === "string") loose.push(d);
      continue;
    }
    const field = String(d.field || "").replace(/^pricingTemplates(\[\d+\])?\.?/, "");
    if (Number.isInteger(d.index) && ["templateId", "name", "isDefault", "active"].includes(field)) {
      rowErrors[d.index] = { ...(rowErrors[d.index] || {}), [field]: d.message || "Invalid" };
    } else if (d.message) loose.push(d.message);
  }
  const formError = loose.length ? loose.join("; ") : details.length ? null : body?.error || null;
  return { rowErrors, formError };
}

/**
 * Is this PUT refusal about the pricing templates? The page saves every
 * processor field in one PUT, so a plain `{ error }` about the name must not be
 * pinned onto the templates section.
 */
export function isPricingTemplateError(body) {
  const details = Array.isArray(body?.details) ? body.details : [];
  if (details.some((d) => d && typeof d === "object" && (Number.isInteger(d.index) || /pricing|template/i.test(String(d.field || ""))))) {
    return true;
  }
  return /pricing|template|isDefault/i.test(String(body?.error || ""));
}

// ── Transmit dialog ────────────────────────────────────────────────────────

/** The dry run's active templates, normalised; ids as strings. */
export function transmitTemplateOptions(dryRun) {
  return (Array.isArray(dryRun?.pricingTemplates) ? dryRun.pricingTemplates : [])
    .filter((t) => t && t.templateId !== undefined && t.templateId !== null && t.templateId !== "")
    .map((t) => ({
      templateId: String(t.templateId),
      // Sent back exactly as received (a Number for Kurv's numeric ids).
      rawId: t.templateId,
      name: str(t.name),
      isDefault: t.isDefault === true,
    }));
}

/** The id to put in the request for a selected (string) id: the raw value. */
export function templateIdForRequest(options, selected) {
  if (selected === null || selected === undefined || selected === "") return null;
  const hit = (options || []).find((o) => sameTemplateId(o.templateId, selected));
  return hit ? hit.rawId : null;
}

/**
 * Which template the select shows. The operator's own pick wins; otherwise the
 * server's `selectedPricingTemplateId` (which IS the default when one exists).
 * Null otherwise — in particular when the processor has active templates but
 * NO default: the API then reports a blocker and the user must choose, so
 * nothing is preselected. A pick that is no longer offered falls back too.
 */
export function resolveTemplateSelection(options, picked, serverSelected) {
  const find = (id) => (options || []).find((o) => sameTemplateId(o.templateId, id));
  return find(picked)?.templateId || find(serverSelected)?.templateId || null;
}

export function templateLabel(t) {
  if (!t) return "";
  return t.name ? `${t.name} (${t.templateId})` : String(t.templateId);
}
